import { useMemo, useCallback } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import updateUtils from 'actions/updateUtils';
import getExpectedPayments, { expectedPaymentsParamsType } from 'queries/expectedPayments/getExpectedPayments';
import { TargetsType } from 'types/CompanyTypes';

import useActionsWithFetchingState from '../useActionsWithFetchingState';

import { arraySelector, utilsSelector } from './selectors';

export interface expectedOrderType {
  order_id: string;
  order_title: string;
  order_number: string;
  status: string;
  currency: string;
  commission: number;
  principal_amount: number;
  default_interest: number;
}

export interface expectedPaymentsDataType {
  payment_date: string;
  company_name: string;
  company_id: string;
  orders: expectedOrderType[];
  total: number;
}

export interface expectedPaymentsAllDataType {
  report: expectedPaymentsDataType[];
  total_eur: number;
  total_usd: number;
}

const initialExpectedPaymentsTableParams: expectedPaymentsParamsType = {};
const initialExpectedPaymentsRequestError = false;

const latePayments = [
  { key: 'false', name: 'tables.expectedPaymentsTable.overduePayments', active: true },
  { key: 'true', name: 'tables.expectedPaymentsTable.currentPayments', active: true },
] as TargetsType[];

const listTarget = [
  { key: 'bank_fee', name: 'tables.expectedPaymentsTable.bankFee', active: true },
  { key: 'interest', name: 'tables.expectedPaymentsTable.commission', active: true },
  { key: 'loan_body', name: 'tables.expectedPaymentsTable.principalAmount', active: true },
  { key: 'penalty', name: 'tables.expectedPaymentsTable.defaultInterest', active: true },
] as TargetsType[];

const useExpectedPayments = () => {
  const selector = useCallback(
    (state) => ({
      listTarget,
      latePayments,
      isPending: state.queries.expectedPaymentsData?.isPending,
      expectedPaymentsData: arraySelector(state, 'expectedPaymentsData') as expectedPaymentsAllDataType,
      expectedPaymentsRequestError: utilsSelector(
        state,
        'expectedPaymentsRequestError',
        initialExpectedPaymentsRequestError,
      ) as boolean,
      expectedPaymentsTableParams: utilsSelector(
        state,
        'expectedPaymentsTableParams',
        initialExpectedPaymentsTableParams,
      ) as expectedPaymentsParamsType,
    }),
    [],
  );

  const dispatch = useDispatch();

  const data = useSelector(selector);

  const actionCreators = useMemo(
    () => ({
      getExpectedPayments,
    }),
    [],
  );

  const expectedPaymentsTableActions = useMemo(
    () =>
      bindActionCreators(
        {
          setExpectedPaymentsTableParams: (expectedPaymentsTableParams: expectedPaymentsParamsType) =>
            updateUtils({
              expectedPaymentsTableParams,
            }),
          setExpectedPaymentsRequestError: (expectedPaymentsRequestError: boolean) =>
            updateUtils({
              expectedPaymentsRequestError,
            }),
        },
        dispatch,
      ),
    [dispatch],
  );

  const [actions, isFetchingState] = useActionsWithFetchingState(actionCreators, dispatch);

  return {
    ...data,
    ...actions,
    ...isFetchingState,
    ...expectedPaymentsTableActions,
  };
};

export default useExpectedPayments;
